/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  PropertyDefinitions,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue, DragSource } from './DragSource';
import { CommonProps } from '../../layout/Sidebar/panels/PropertiesView/CommonProperties';

export const DragSourceProps: PropertyDefinitions<
  Omit<DragSource, keyof CommonProps>
> = {
  type: {
    label: 'Type',
    type: 'string',
    helpText:
      'You can specify a type value for this drag source. Only the Drop Targets with the same type will react to this item.',
    required: true,
  },
  canDragCondition: {
    label: 'Can Drag Condition',
    type: 'expressionOnly',
    returnType: () => 'boolean',
  },
  canDrag: {
    label: 'canDrag Function',
    type: 'controllerFunction',
    category: 'Controllers',
  },
  beginDrag: {
    label: 'beginDrag Function',
    type: 'controllerFunction',
    category: 'Controllers',
  },
};

const DragSourceManager: ComponentManagerFn<'DragSource'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'DragSource'>(platform, 'DragSource'),
    type: 'DragSource',
    name: 'Drag Source',
    isChildAllowed: (page, item, childType) =>
      (['RowPanel', 'Card'] as ItemType[]).includes(childType),
    defaultValue,
    props: DragSourceProps,
    // getDisplayText: (item) => item.props.icon?.iconName ?? 'Choose icon!',
    category: 'Containers',
    icon: { iconName: 'hand-rock' },
    description: `**Overview**<br/>
The Drag Source is an interactive wrapper component that enables "drag-and-drop" functionality for its child content. By wrapping visual elements—specifically Cards or Row Panels—inside a Drag Source, you transform them from static displays into movable objects that users can click, drag, and reposition across the browser window.
This component is the "initiator" half of the drag-and-drop workflow. It works in tandem with Drop Target components to build interactive interfaces like Kanban boards, reorderable lists, or visual file managers.

**1. Defining Compatibility (The Handshake)**<br/>
For drag-and-drop to work safely, the system needs to know which items fit into which destination. This is handled by a simple matching system.
* **Type:** A mandatory string identifier (e.g., 'task-card', 'inventory-item'). This acts as a security key; the dragged item can only be dropped onto a Drop Target that has been configured with this exact same Type. This prevents users from accidentally dropping a "User Profile" into a "Trash Can" unless you explicitly allow it.

**2. Controlling Interaction Rules**<br/>
Not every item should be movable at all times. These properties allow you to dynamically enable or disable the drag behavior based on the application state.
* **Can Drag Condition:** A boolean expression (True/False). Use this for simple toggles—for example, binding it to a variable so that items cannot be dragged when the application is in "Read-Only Mode."
* **canDrag Function:** A controller function that allows for complex logic. Use this if you need to run a calculation or check specific data attributes (e.g., "Prevent dragging if the Task Status is 'Completed'") before allowing the user to pick up the item.

**3. Event Handling**<br/>
You can hook into the lifecycle of the drag interaction to manage data or trigger side effects.
* **beginDrag Function:** A logic block that executes the moment the user starts dragging the component. This is typically used to prepare or "snapshot" the data associated with the item being moved, ensuring the Drop Target knows exactly what it is receiving.

**4. Structure & Content**<br/>
The Drag Source is purely functional—it has no visual appearance of its own (no padding, borders, or colors).
* **Wrapper Requirement:** To function, the Drag Source must wrap a visual component.
* **Allowed Children:** Currently, this component is optimized to wrap **Cards** (common for Kanban boards) and **Row Panels** (common for reorderable lists).`,
  };
};

export default DragSourceManager;
